import { DIMENSION_LABELS } from "@/lib/ramsCheck";

/* Animated progress for a streaming RAMS review: a thin bar fills as the eight
   dimensions arrive, the active check carries a spinner, completed checks pop
   their tick in (keyframes in globals.css). Shared by the rejection checker
   tool and the in-builder review. */
export function CheckProgress({ dimsArrived }: { dimsArrived: number }) {
  return (
    <div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-mist" aria-hidden>
        <div
          className="h-full rounded-full bg-emerald transition-all duration-700 ease-out"
          style={{ width: `${Math.max(4, (dimsArrived / DIMENSION_LABELS.length) * 100)}%` }}
        />
      </div>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        {DIMENSION_LABELS.map((label, i) => {
          const done = i < dimsArrived;
          const active = i === dimsArrived;
          return (
            <li
              key={label}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 text-[0.84rem] transition-colors duration-300 ${
                done
                  ? "border-emerald/40 bg-emerald-tint text-emerald-deep"
                  : active
                    ? "border-emerald/40 bg-mist text-ink"
                    : "border-line bg-white text-muted"
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.7rem] font-bold ${
                  done ? "bg-emerald text-white" : "bg-mist text-muted"
                }`}
                aria-hidden
              >
                {done ? (
                  <span className="animate-tick-pop">✓</span>
                ) : active ? (
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-emerald border-t-transparent" />
                ) : (
                  i + 1
                )}
              </span>
              <span className={done ? "font-semibold" : ""}>{label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Three staggered bouncing dots for "Reviewing…" headlines. */
export function ReviewingDots() {
  return (
    <span className="ml-1 inline-flex gap-0.5 align-baseline" aria-hidden>
      {[0, 1, 2].map((d) => (
        <span
          key={d}
          className="h-1 w-1 animate-bounce rounded-full bg-current"
          style={{ animationDelay: `${d * 150}ms` }}
        />
      ))}
    </span>
  );
}
